import type { WireSite } from './wire';

/**
 * Which word a shop's category listings live under: `/c/panels`, `/categories/panels`,
 * `/collections/kitchen-doors`.
 *
 * <p><strong>Why a fixed set rather than free text.</strong> A listing URL is a Next route
 * segment, and the routes are part of the build — `app/[locale]/(catalogue)/categories/[...path]`
 * is a directory, not a value. So the WORDS a deployment can serve are fixed here, and the
 * per-tenant setting chooses among them. Changing a shop from `collections` to `categories` is a
 * settings edit; inventing a sixth word is a deploy. That is the honest division, and it is the
 * right way round: the first is what an operator wants to do and the second almost never happens.
 *
 * <p>The alternative — one dynamic `[listing]` segment validated at request time — reads better
 * and is a trap. `[listing]/[...path]` sits beside `[locale]/[...slug]`, the catch-all that
 * renders every operator-authored page, and a dynamic two-segment route would capture
 * `/about/our-team` before the catch-all ever saw it. Static segments cannot: Next matches them
 * first and only them.
 *
 * <p>Every word in this list is served by this build, and exactly ONE of them renders for a given
 * shop — its canonical. The others answer a permanent redirect to it, so an old `/c/panels` link,
 * a typed singular, and the word the other shop uses all arrive at the one indexable URL rather
 * than at a 404 or, worse, at a second copy of the page.
 */
export const LISTING_PREFIXES = [
  'c',
  'categories',
  'category',
  'collections',
  'collection',
] as const;

export type ListingPrefix = (typeof LISTING_PREFIXES)[number];

/**
 * What a shop gets when it has said nothing — and what every shop was on before this was
 * configurable, so an unset value must keep serving the URLs that are already indexed.
 */
export const DEFAULT_LISTING_PREFIX: ListingPrefix = 'c';

const KNOWN = new Set<string>(LISTING_PREFIXES);

export function isListingPrefix(value: string | null | undefined): value is ListingPrefix {
  return !!value && KNOWN.has(value);
}

/**
 * The shop's canonical listing word.
 *
 * <p>An unrecognised setting falls back to the default rather than throwing or 404ing every
 * listing: a typo in one organisation-config row must not take the catalogue of a trading shop
 * off the air. It is a word this build does not serve, so there is nothing to serve it with.
 */
export function listingPrefixFor(site: WireSite | null | undefined): ListingPrefix {
  const configured = site?.listingPrefix?.trim().toLowerCase();
  return isListingPrefix(configured) ? configured : DEFAULT_LISTING_PREFIX;
}
